import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { Pause, PlayArrow } from '@mui/icons-material';

export interface PreviewVideoPlayerProps {
  url: string;
  muted?: boolean;
  loop?: boolean;
  autoPlay?: boolean;
  onEnded?: () => void;
}

function formatPlaybackTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function isLikelySpuriousMediaEnded(element: HTMLMediaElement): boolean {
  if (!Number.isFinite(element.currentTime) || element.currentTime > 0.5) {
    return false;
  }
  if (!Number.isFinite(element.duration) || element.duration <= 0) {
    return true;
  }
  return element.duration - element.currentTime > 0.5;
}

function PreviewVideoPlayerInner({
  url,
  muted = true,
  loop = false,
  autoPlay = true,
  onEnded,
}: PreviewVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = muted;
  }, [muted]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const handleSeek = useCallback((_: Event, value: number | number[]) => {
    const nextTime = Array.isArray(value) ? value[0]! : value;
    setCurrentTime(nextTime);
    setSeeking(true);
  }, []);

  const handleSeekCommitted = useCallback((_: Event | React.SyntheticEvent, value: number | number[]) => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const nextTime = Array.isArray(value) ? value[0]! : value;
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
    setSeeking(false);
  }, []);

  const handleEnded = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      if (!onEnded || isLikelySpuriousMediaEnded(event.currentTarget)) {
        return;
      }
      onEnded();
    },
    [onEnded],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        loop={loop}
        onEnded={loop ? undefined : handleEnded}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          setDuration(video.duration);
          setCurrentTime(video.currentTime);
        }}
        onTimeUpdate={(event) => {
          if (seeking) {
            return;
          }
          setCurrentTime(event.currentTarget.currentTime);
        }}
        onDurationChange={(event) => {
          setDuration(event.currentTarget.duration);
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
        src={url}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          bgcolor: 'rgba(0,0,0,0.72)',
        }}
      >
        <IconButton size="small" onClick={togglePlay} sx={{ color: 'white' }} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
        </IconButton>
        <Slider
          size="small"
          min={0}
          max={duration > 0 ? duration : 100}
          step={0.1}
          value={Math.min(currentTime, duration > 0 ? duration : 100)}
          onChange={handleSeek}
          onChangeCommitted={handleSeekCommitted}
          sx={{
            'flex': 1,
            'color': 'white',
            'mx': 0.5,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
          }}
          aria-label="Video progress"
        />
        <Typography variant="caption" sx={{ color: 'white', minWidth: 72, textAlign: 'right', flexShrink: 0 }}>
          {formatPlaybackTime(currentTime)} / {formatPlaybackTime(duration)}
        </Typography>
      </Box>
    </Box>
  );
}

export function PreviewVideoPlayer(props: PreviewVideoPlayerProps) {
  return <PreviewVideoPlayerInner key={props.url} {...props} />;
}
